/**
 * Photo acquisition port — camera and gallery (M5 / S05-02).
 *
 * Risk Probe C proved on real Android hardware that the ordinary web file
 * input handles all three paths this product needs — rear camera via
 * `capture="environment"`, gallery selection, and multi-select — so **no
 * `@capacitor/camera` dependency is justified**. That was a measured finding,
 * not a preference, and this port exists so the conclusion is expressed once
 * and can be revisited in one place if iOS ever disagrees.
 *
 * **The camera and the gallery are two different configurations of that one
 * input, and they must never collapse into one** — see `ACCEPT_CAMERA` below
 * for the Android rule that decides which is which, and
 * `photo-source.test.ts` for the tests that hold them apart.
 *
 * Feature code never touches an `<input type="file">` directly and never
 * imports `@capacitor/*` (CLAUDE.md §8). It asks for photos; the port decides
 * how they arrive.
 *
 * This port ACQUIRES ONLY. It does not inspect, sanitize, verify or persist —
 * the bytes it hands back are the untrusted original, and the only safe thing
 * to do with them is pass them to `processPhoto`.
 */

export type PhotoSourceKind = 'camera' | 'library';

export interface AcquiredPhoto {
  /** The ORIGINAL bytes, untrusted and unsanitized. */
  readonly bytes: Uint8Array;
  /** What the picker CLAIMED. Checked against the bytes downstream, never trusted. */
  readonly declaredMime: string;
  /** Plain filename from the picker. Never a filesystem path. */
  readonly name: string;
}

export interface PhotoSourcePort {
  /**
   * Opens the picker and resolves with what the user chose. An empty array
   * means they cancelled — which is not an error and must not be reported as
   * one.
   */
  readonly pick: (
    kind: PhotoSourceKind,
    options?: { multiple?: boolean },
  ) => Promise<AcquiredPhoto[]>;
}

/**
 * `accept` narrows the picker to the two formats v1 supports. It is a
 * convenience for the user, NOT a security control — a picker may return
 * anything regardless, which is why the format is sniffed from the bytes
 * downstream and a declared type that disagrees is refused.
 */
export const ACCEPT_LIBRARY = 'image/jpeg,image/png';

/**
 * THE CAMERA PATH MUST SAY `image/*`, AND THAT IS NOT A STYLE CHOICE.
 *
 * Inside a Capacitor Android app the WebView does not open a picker itself —
 * it hands the request to `BridgeWebChromeClient.onShowFileChooser`, which
 * decides between the camera and the gallery with this line
 * (`@capacitor/android` 8.5.0, `BridgeWebChromeClient.java:283`):
 *
 *   boolean capturePhoto = captureEnabled && acceptTypes.contains("image/*");
 *
 * `acceptTypes` is our accept attribute split on commas, and `contains` is
 * EXACT STRING EQUALITY. So `image/jpeg,image/png` — however correct as a
 * description of what we support — does not match, `capturePhoto` is false,
 * and the camera branch is never entered: the request falls through to the
 * ordinary file picker. **That is exactly what happened on Jerry's Infinix on
 * 2026-08-14: both "Take photo" and "Choose from gallery" opened the gallery,
 * and no camera-permission prompt appeared — because the prompt lives inside
 * the branch we never reached.**
 *
 * Widening this to `image/*` costs nothing real: the camera returns one photo
 * in whatever format the device writes, and the pipeline sniffs and refuses
 * anything unsupported (HEIC included) with its own message. The GALLERY list
 * stays narrow, so what Brandon is offered to browse is unchanged.
 *
 * Risk Probe C proved a camera photo could be ACQUIRED on real Android through
 * a plain file input. Its harness was never committed, so nothing recorded
 * which accept value it used — the narrow list arrived later with this port,
 * and the device pass is what caught the difference.
 */
export const ACCEPT_CAMERA = 'image/*';

export function createWebPhotoSource(): PhotoSourcePort {
  return {
    pick: async (kind, options) =>
      await new Promise<AcquiredPhoto[]>((resolve) => {
        const input = document.createElement('input');
        input.type = 'file';
        if (kind === 'camera') {
          // BOTH halves are required together — see ACCEPT_CAMERA above. The
          // capture attribute alone is not enough, and neither is the accept
          // value alone.
          input.accept = ACCEPT_CAMERA;
          // Asks Android for the rear camera. Ignored where unsupported, which
          // degrades to an ordinary picker rather than failing.
          input.setAttribute('capture', 'environment');
        } else {
          input.accept = ACCEPT_LIBRARY;
          if (options?.multiple === true) {
            input.multiple = true;
          }
        }
        input.style.display = 'none';

        let settled = false;
        const finish = (photos: AcquiredPhoto[]): void => {
          if (settled) return;
          settled = true;
          input.remove();
          resolve(photos);
        };

        input.addEventListener('change', () => {
          const files = Array.from(input.files ?? []);
          void Promise.all(
            files.map(async (file) => ({
              bytes: new Uint8Array(await file.arrayBuffer()),
              declaredMime: file.type,
              name: file.name,
            })),
          ).then(finish, () => {
            finish([]);
          });
        });

        // Cancelling fires no `change` event in most engines. `cancel` is
        // supported in current Chromium and Safari; where it is not, the input
        // is simply collected with the screen and the promise never settles —
        // which is why callers must not block anything on it.
        input.addEventListener('cancel', () => {
          finish([]);
        });

        document.body.appendChild(input);
        input.click();
      }),
  };
}

/**
 * Selected once. Both runtimes use the same implementation — the plain file
 * input is a standard capability inside the Chromium WebView Capacitor uses on
 * Android and inside WKWebView on iOS, and Probe C exercised this exact path on
 * real Android hardware. iOS remains unproven (I-016, M14).
 */
export function selectPhotoSource(): PhotoSourcePort {
  return createWebPhotoSource();
}
